import { useState, useEffect, useRef, FormEvent } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

export default function Navbar() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { isLoaded, isSignedIn, signOut } = useAuth();
  const { user } = useUser();
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const term = q.trim();
    if (!term) return;
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(term)}`);
  }

  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `text-sm font-medium transition-colors ${
      isActive ? 'text-cinema-gold' : 'text-cinema-gray hover:text-cinema-white'
    }`;

  const firstName = user?.firstName || user?.username || (user?.primaryEmailAddress?.emailAddress ?? '').split('@')[0] || 'Account';

  return (
    <header className="sticky top-0 z-40 bg-cinema-black/90 backdrop-blur border-b border-cinema-surface">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" aria-label="CinemaKE home">
          <div className="w-8 h-8 rounded-md bg-cinema-gold text-cinema-black grid place-items-center font-black text-sm">
            CK
          </div>
          <span className="hidden sm:inline text-lg font-bold tracking-tight">
            Cinema<span className="text-cinema-gold">KE</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6">
          <NavLink to="/" end className={linkClass}>
            Movies
          </NavLink>
          <NavLink to="/cinemas" className={linkClass}>
            Cinemas
          </NavLink>
        </nav>

        <form
          onSubmit={onSubmit}
          role="search"
          className="flex-1 max-w-xs hidden sm:block"
        >
          <div className="relative">
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search movies..."
              className="w-full h-9 pl-9 pr-3 rounded-full bg-cinema-surface text-sm placeholder:text-cinema-muted focus:outline-none focus:ring-2 focus:ring-cinema-gold"
              aria-label="Search movies"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" strokeLinecap="round" />
            </svg>
          </div>
        </form>

        <div className="flex items-center gap-2 shrink-0">
          {isLoaded && !isSignedIn && (
            <Link
              to="/sign-in"
              className="text-sm font-medium text-cinema-gray hover:text-cinema-white px-3 py-2 rounded-md transition-colors"
            >
              Sign In
            </Link>
          )}

          {isLoaded && isSignedIn && (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="w-8 h-8 rounded-full bg-cinema-gold text-cinema-black grid place-items-center text-sm font-bold hover:bg-cinema-gold/90"
                aria-label="Account menu"
                aria-expanded={menuOpen}
              >
                {firstName.charAt(0).toUpperCase()}
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-44 rounded-xl border border-cinema-surface bg-cinema-black shadow-xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-cinema-surface">
                    <p className="text-sm font-medium truncate">{firstName}</p>
                    <p className="text-xs text-cinema-gray truncate">
                      {user?.primaryEmailAddress?.emailAddress ?? ''}
                    </p>
                  </div>
                  <Link
                    to="/account"
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2.5 text-sm text-cinema-gray hover:text-cinema-white hover:bg-cinema-surface transition-colors"
                  >
                    My Account
                  </Link>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      signOut();
                    }}
                    className="block w-full text-left px-4 py-2.5 text-sm text-cinema-gray hover:text-cinema-white hover:bg-cinema-surface transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="sm:hidden p-2 rounded-md text-cinema-gray hover:text-cinema-white"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {open ? (
                <path d="M6 6l12 12M6 18L18 6" strokeLinecap="round" />
              ) : (
                <>
                  <path d="M4 7h16" strokeLinecap="round" />
                  <path d="M4 12h16" strokeLinecap="round" />
                  <path d="M4 17h16" strokeLinecap="round" />
                </>
              )}
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="sm:hidden border-t border-cinema-surface bg-cinema-black">
          <div className="px-4 py-3 space-y-3">
            <form onSubmit={onSubmit} role="search">
              <input
                ref={inputRef}
                type="search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search movies..."
                className="w-full h-10 px-3 rounded-md bg-cinema-surface text-sm placeholder:text-cinema-muted focus:outline-none focus:ring-2 focus:ring-cinema-gold"
              />
            </form>
            <div className="flex flex-col gap-3">
              <Link to="/" onClick={() => setOpen(false)} className="text-sm font-medium text-cinema-gray">
                Movies
              </Link>
              <Link to="/cinemas" onClick={() => setOpen(false)} className="text-sm font-medium text-cinema-gray">
                Cinemas
              </Link>
              {isLoaded && !isSignedIn && (
                <Link
                  to="/sign-in"
                  onClick={() => setOpen(false)}
                  className="text-sm font-medium text-cinema-gray"
                >
                  Sign In
                </Link>
              )}
              {isLoaded && isSignedIn && (
                <>
                  <Link
                    to="/account"
                    onClick={() => setOpen(false)}
                    className="text-sm font-medium text-cinema-gray"
                  >
                    My Account
                  </Link>
                  <button
                    onClick={() => {
                      setOpen(false);
                      signOut();
                    }}
                    className="text-left text-sm font-medium text-cinema-gray"
                  >
                    Sign Out
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

